/**
 * AllFaxes — searchable history of every fax (both directions) for the network
 * (the "All faxes" sub-tab of the Fax tab). Backed by GET /api/crm/fax.
 *
 * Filter by direction + status, search by number / reference code / matched
 * patient name (server-side, debounced). Rows open a PDF preview in a modal
 * (same blob proxy as the triage queue — the PHI bucket has no browser CORS).
 * A failed standalone fax can be retried in place (POST /fax/:id/retry).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { ArrowDownLeft, ArrowUpRight, Download, Loader2, RefreshCw, X } from 'lucide-react';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import api from '../../../services/api';
import { FaxRecord } from '../../types';
import { downloadCrmDocument, formatPhone, relTime, usePersistedState } from '../../lib';
import { FilterBar, FilterChip, SearchInput } from '../../components';
import { Alert, Z } from '../ui';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

const PAGE_SIZE = 50;

type FaxDirection = 'outbound' | 'inbound';

const STATUS_STYLES: Record<string, string> = {
  delivered: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  matched: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  sending: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300',
  queued: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300',
  received: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  needs_match: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  discarded: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
};

function StatusPill({ status }: { status: string }) {
  const cls = STATUS_STYLES[status] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  return (
    <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

/** Modal PDF preview, fetched as same-origin bytes (no CORS on the PHI bucket). */
function FaxPreview({ fax, onClose }: { fax: FaxRecord; onClose: () => void }) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!fax.document_id) { setLoading(false); setError('No document is attached to this fax.'); return; }
    let objectUrl: string | null = null;
    let alive = true;
    setLoading(true);
    api.get(`/api/crm/documents/${fax.document_id}/file`, { responseType: 'blob' })
      .then((res) => { if (alive) { objectUrl = URL.createObjectURL(res.data); setPdfUrl(objectUrl); } })
      .catch(async (e) => {
        let msg = errMsg(e);
        const d = e?.response?.data;
        if (d instanceof Blob) {
          try { const p = JSON.parse(await d.text()); msg = p.detail || p.error || msg; } catch { /* keep */ }
        }
        if (alive) setError(msg);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [fax.document_id]);

  return (
    <div className={`fixed inset-0 ${Z.modalRaised} flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4`} onClick={onClose}>
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-xl bg-white shadow-xl dark:bg-gray-800 sm:rounded-xl" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="flex items-center justify-between gap-2 border-b border-gray-200 p-4 dark:border-gray-700">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
              {fax.direction === 'inbound' ? 'From' : 'To'} {formatPhone(fax.direction === 'inbound' ? fax.from_number : fax.to_number) || 'Unknown'}
            </p>
            <p className="truncate text-xs text-gray-400">{fax.document_filename || 'fax.pdf'} · {relTime(fax.created_at)}</p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {pdfUrl && (
              <a
                href={pdfUrl}
                download={fax.document_filename || `fax-${fax.id}.pdf`}
                title="Download this fax (PDF)"
                aria-label="Download this fax"
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
              >
                <Download className="h-3.5 w-3.5" /> Download
              </a>
            )}
            <button type="button" onClick={onClose} aria-label="Close preview" className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900/40">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
          ) : error ? (
            <p className="p-6 text-center text-sm text-red-600 dark:text-red-400">{error}</p>
          ) : pdfUrl ? (
            <Document
              file={pdfUrl}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              onLoadError={() => setError('Could not render the fax PDF.')}
              loading={<div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>}
            >
              {Array.from({ length: numPages }, (_, i) => (
                <div key={i} className="border-b border-gray-200 last:border-0 dark:border-gray-700">
                  <Page pageNumber={i + 1} width={Math.min(window.innerWidth - 48, 720)} renderTextLayer={false} renderAnnotationLayer={false} />
                </div>
              ))}
            </Document>
          ) : (
            <p className="py-16 text-center text-sm text-gray-400">No preview available.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AllFaxes() {
  const [faxes, setFaxes] = useState<FaxRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const [query, setQuery] = usePersistedState('fax.allQuery', '');
  // Multi-select direction (empty = all). Key bumped to v2: the old pref held a
  // scalar DirFilter and would restore a string into this array-typed state.
  const [directions, setDirections] = usePersistedState<string[]>('fax.allDirectionv2', []);
  const [preview, setPreview] = useState<FaxRecord | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchPage = useCallback(async (offset: number, replace: boolean) => {
    if (replace) setLoading(true); else setLoadingMore(true);
    try {
      const params: Record<string, string | number> = { limit: PAGE_SIZE, offset };
      if (query.trim()) params.q = query.trim();
      if (directions.length) params.direction = directions.join(',');
      const res = await api.get('/api/crm/fax', { params });
      const rows: FaxRecord[] = res.data?.jobs || [];
      setFaxes((prev) => (replace ? rows : [...prev, ...rows]));
      setHasMore(rows.length === PAGE_SIZE);
      setError(null);
    } catch (e: any) {
      if (replace) setError(errMsg(e));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [query, directions]);

  // Debounced reload whenever the query or direction filter changes.
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => fetchPage(0, true), 300);
    return () => { if (debounce.current) clearTimeout(debounce.current); };
  }, [fetchPage]);

  const retry = async (fax: FaxRecord) => {
    if (retryingId) return;
    setRetryingId(fax.id);
    setNotice(null);
    try {
      await api.post(`/api/crm/fax/${fax.id}/retry`);
      setNotice('Fax re-queued.');
      fetchPage(0, true);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setRetryingId(null);
    }
  };

  const download = async (fax: FaxRecord) => {
    if (!fax.document_id || downloadingId) return;
    setDownloadingId(fax.id);
    setError(null);
    try {
      await downloadCrmDocument(fax.document_id, fax.document_filename || `fax-${fax.id}.pdf`);
    } catch (e: any) {
      // responseType:'blob' delivers JSON error bodies as a Blob — recover the reason.
      let msg = errMsg(e);
      const d = e?.response?.data;
      if (d instanceof Blob) {
        try { const p = JSON.parse(await d.text()); msg = p.detail || p.error || msg; } catch { /* keep */ }
      }
      setError(msg);
    } finally {
      setDownloadingId(null);
    }
  };

  const toggleDir = (key: FaxDirection) =>
    setDirections(directions.includes(key) ? directions.filter((d) => d !== key) : [...directions, key]);
  const dirChip = (key: FaxDirection, label: string) => (
    <FilterChip active={directions.includes(key)} onClick={() => toggleDir(key)}>
      {label}
    </FilterChip>
  );

  return (
    <div className="space-y-4">
      <FilterBar>
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder="Search by number, reference code, or patient…"
          label="Search faxes"
          className="flex-1 basis-56"
        />
        <FilterChip active={directions.length === 0} onClick={() => setDirections([])}>All</FilterChip>
        {dirChip('outbound', 'Sent')}
        {dirChip('inbound', 'Received')}
      </FilterBar>

      {notice && (
        <div className="rounded-lg border border-primary-200 bg-primary-50 p-2.5 text-xs text-primary-800 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300">{notice}</div>
      )}
      {error && (
        <Alert tone="error" icon={false}>{error}</Alert>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary-600" /></div>
      ) : faxes.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">No faxes found.</p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {query.trim() || directions.length > 0 ? 'Try a different search or filter.' : 'Sent and received faxes will show up here.'}
          </p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {faxes.map((f) => {
            const number = f.direction === 'inbound' ? f.from_number : f.to_number;
            const canRetry = f.is_standalone && f.direction === 'outbound' && f.status === 'failed';
            return (
              <div key={f.id} className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
                <div className="flex items-center gap-3">
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                    f.direction === 'inbound'
                      ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300'
                      : 'bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-300'
                  }`}>
                    {f.direction === 'inbound' ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                  </span>
                  <button
                    type="button"
                    onClick={() => f.document_id && setPreview(f)}
                    disabled={!f.document_id}
                    className="min-w-0 flex-1 text-left disabled:cursor-default"
                  >
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                        {formatPhone(number) || 'Unknown'}
                      </span>
                      <StatusPill status={f.status} />
                      {f.is_standalone && (
                        <span className="shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-400">standalone</span>
                      )}
                    </div>
                    <p className="mt-0.5 truncate text-xs text-gray-400">
                      {f.contact ? (
                        <Link to={`/crm/contacts/${f.contact.id}`} onClick={(e) => e.stopPropagation()} className="text-primary-700 hover:underline dark:text-primary-400">
                          {f.contact.name}
                        </Link>
                      ) : f.note ? f.note : f.reference_code || (f.direction === 'inbound' ? 'Inbound fax' : 'Outbound fax')}
                      {' · '}{relTime(f.created_at)}
                      {f.page_count ? ` · ${f.page_count} pg` : ''}
                    </p>
                    {f.status === 'failed' && f.error && (
                      <p className="mt-0.5 truncate text-xs text-red-600 dark:text-red-400">{f.error}</p>
                    )}
                  </button>
                  {f.document_id && (
                    <button
                      type="button"
                      onClick={() => download(f)}
                      disabled={downloadingId === f.id}
                      title="Download this fax (PDF)"
                      aria-label="Download this fax"
                      className="inline-flex min-h-[44px] shrink-0 items-center gap-1 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                    >
                      {downloadingId === f.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                      <span className="hidden sm:inline">Download</span>
                    </button>
                  )}
                  {canRetry && (
                    <button
                      type="button"
                      onClick={() => retry(f)}
                      disabled={retryingId === f.id}
                      className="inline-flex min-h-[44px] shrink-0 items-center gap-1 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                    >
                      {retryingId === f.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                      Retry
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {hasMore && (
            <div className="pt-2 text-center">
              <button
                type="button"
                onClick={() => fetchPage(faxes.length, false)}
                disabled={loadingMore}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
              >
                {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
                Load more
              </button>
            </div>
          )}
        </div>
      )}

      {preview && <FaxPreview fax={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
